import { SectionLabel } from "@/components/SectionLabel";
import { WordReveal } from "@/components/WordReveal";
import {
  FORMATS,
  TALKS,
  isUpcoming,
  mailtoBooking,
  sortByDateDesc,
  type Talk,
} from "@/content/speaking";
import { assetPath } from "@/lib/assets";

const DATE_FMT = new Intl.DateTimeFormat("nb-NO", {
  year: "numeric",
  month: "short",
  day: "2-digit",
});

export function Speaking() {
  const sorted = sortByDateDesc(TALKS);

  return (
    <section
      id="speaking"
      className="relative scroll-mt-20 overflow-hidden border-t border-line py-20"
      aria-labelledby="speaking-h"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <header className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div className="max-w-xl">
            <SectionLabel index={2}>Foredrag</SectionLabel>
            <WordReveal
              as="h2"
              id="speaking-h"
              className="mt-5 block font-display text-5xl font-normal leading-[1.02] md:text-6xl"
            >
              Foredrag, workshops og samtaler.
            </WordReveal>

            <div className="mt-8 flex flex-wrap gap-2">
              {FORMATS.map((format) => (
                <span
                  key={format}
                  className="border border-line bg-panel px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-muted"
                >
                  {format}
                </span>
              ))}
            </div>

            <a
              href={mailtoBooking()}
              className="mt-8 inline-flex items-center gap-3 border border-accent bg-accent px-5 py-3 font-mono text-xs uppercase tracking-widest text-bg transition-colors hover:bg-transparent hover:text-accent focus-visible:bg-transparent focus-visible:text-accent focus-visible:outline-none"
            >
              <span>Bestill foredrag</span>
              <span aria-hidden>→</span>
            </a>
          </div>

          <div className="grid gap-7 border-y border-line py-7 md:grid-cols-[minmax(12rem,16rem)_1fr] md:items-start md:gap-9">
            <div className="relative w-44 overflow-hidden border border-line bg-panel md:w-full">
              <img
                src={assetPath("/speaking/per-arne-speaking.png")}
                alt="Dr. Per-Arne Andersen holder foredrag på en scene"
                className="aspect-[3/4] w-full object-cover"
                loading="lazy"
              />
            </div>

            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted">
                Når AI møter praksis
              </p>
              <p className="mt-4 max-w-prose text-lg leading-relaxed text-muted md:text-xl">
                Jeg holder foredrag om kunstig intelligens slik den faktisk møter mennesker, arbeid
                og samfunn: hva teknologien kan brukes til, hvor den feiler, og hvilke valg vi må ta
                når algoritmer får innflytelse.
              </p>
              <p className="mt-5 max-w-prose text-base leading-relaxed text-muted md:text-lg">
                Med erfaring fra teknologiutvikling og kunstig intelligens ser jeg AI som mer enn
                kode. Det handler også om organisering, ansvar, kompetanse og tillit, særlig når
                teknologien tas i bruk i virksomheter og offentlige tjenester.
              </p>
            </div>
          </div>
        </header>
      </div>

      <div className="mx-auto mt-16 max-w-7xl px-6 md:px-10 lg:px-16">
        <div className="mb-6 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h3 className="font-mono text-xs uppercase tracking-widest text-muted">
              Foredrag jeg holder
            </h3>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted md:text-base">
              Alle foredragene er åpne for booking og kan skreddersys til publikum, bransje og
              modenhetsnivå, fra korte inspirasjonsinnlegg til workshops og faglige diskusjoner.
            </p>
          </div>
          <p className="font-mono text-[0.65rem] uppercase tracking-widest text-muted">
            Fra = første gang holdt eller tilgjengelig
          </p>
        </div>

        <ol className="divide-y divide-line border-y border-line">
          {sorted.map((talk) => (
            <TalkRow key={`${talk.date}-${talk.title}`} talk={talk} />
          ))}
        </ol>
      </div>
    </section>
  );
}

function TalkRow({ talk }: { talk: Talk }) {
  const isNew = isUpcoming(talk);
  const content = (
    <div className="grid gap-4 py-6 md:grid-cols-[7.5rem_1fr_auto] md:items-start md:gap-8 md:py-7">
      <time dateTime={talk.date} className="font-mono uppercase tracking-widest text-muted">
        <span className="block text-[0.65rem]">Fra</span>
        <span className="mt-1 block text-xs md:text-sm">
          {DATE_FMT.format(new Date(talk.date))}
        </span>
      </time>

      <div className="min-w-0">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="border border-line bg-panel px-2.5 py-1 font-mono text-[0.65rem] uppercase tracking-widest text-muted">
            {talk.format}
          </span>
          {isNew ? (
            <span className="border border-accent bg-accent px-2.5 py-1 font-mono text-[0.65rem] uppercase tracking-widest text-bg">
              Nytt!
            </span>
          ) : null}
        </div>

        <p className="font-display text-2xl font-normal leading-tight text-fg md:text-[1.9rem]">
          {talk.title}
        </p>
        {hasSpecificVenue(talk) ? (
          <p className="mt-1 text-sm text-muted">
            {talk.venue}
            {talk.location ? <span className="text-muted/70"> · {talk.location}</span> : null}
          </p>
        ) : null}
      </div>

      <span aria-hidden className="hidden font-mono text-xs uppercase text-muted md:block">
        {talk.href ? "↗" : ""}
      </span>
    </div>
  );

  return (
    <li className="hover:bg-panelStrong/40 group transition">
      {talk.href ? (
        <a
          href={talk.href}
          target="_blank"
          rel="noopener noreferrer"
          className="focus-visible:bg-panelStrong/60 block focus-visible:outline-none"
        >
          {content}
        </a>
      ) : (
        content
      )}
    </li>
  );
}

function hasSpecificVenue(talk: Talk) {
  return talk.venue !== "Åpent for booking" || Boolean(talk.location);
}
